import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  ArrowLeft, Bot, Braces, Check, ChevronDown, CircleDot, FilePlus, FileText, Folder, GitCommitHorizontal, ImageIcon, ImagePlus, Play, Plus, RotateCcw, Rocket, ShieldOff, Trash2, Type, Upload, X, Zap, Clock,
} from "lucide-react";
import { api, fileToDataUrl, invalidate, mutateCache, streamPost, timeAgo, toast, toastError, useDebounced, useQuery, useStatus, preview, fmtMs } from "../lib";
import { AutoTextarea, Badge, Button, Dot, Empty, Field, KindIcon, MetaChips, Modal, ModelPicker, OutputView, Seg, Switch, channelTone, cx, usePopover } from "../ui";
import { CodeEditor } from "../editor";
import { SchemaEditor } from "../schema-builder";
import { TraceView } from "../trace";
import { AddCaseModal, ExecutionDrawer, LogsTable } from "./logs";
import { VersionsTab, EvalsTab } from "./target-tabs";
import { DeployModal } from "./deploy-modal";
import { TasteTab } from "./taste";
import type { AgentSpec, ContextFile, Execution, PromptSpec, Spec, TraceEvent, VarType } from "../../shared/types";

const VAR_RE = /\{\{\s*([a-zA-Z_][\w-]*)\s*\}\}/g;
export function syncVars<S extends Spec>(spec: S): S {
  const text = spec.kind === "prompt" ? spec.system + "\n" + spec.prompt : spec.task;
  const names: string[] = [];
  for (const m of text.matchAll(VAR_RE)) if (!names.includes(m[1])) names.push(m[1]);
  const variables: any = {};
  for (const n of names) variables[n] = spec.variables?.[n] ?? { type: "text" };
  return { ...spec, variables };
}

export interface TargetData {
  meta: any;
  draft: { spec: Spec; files: ContextFile[]; hash: string };
  versions: { n: number; hash: string; message: string; createdAt: number }[];
  deployments: any[];
  sessions: any[];
  stats: any;
  evals: any[];
}

export function TargetPage({ p, t, tab }: { p: string; t: string; tab: string }) {
  const path = `/api/projects/${p}/targets/${t}`;
  const { data, error } = useQuery<TargetData>(path, { poll: 6000 });
  const { data: project } = useQuery<any>(`/api/projects/${p}`);
  const search = useSearch();
  const qs = new URLSearchParams(search);
  const refKey = qs.get("v") ?? "draft";
  const [, nav] = useLocation();

  // Publicar e Versões abrem em modais. Os endereços antigos das abas (/deploy, /versoes) abrem o modal no editor.
  const [deployOpen, setDeployOpen] = useState(tab === "deploy");
  const [versionsOpen, setVersionsOpen] = useState(tab === "versoes");
  useEffect(() => {
    if (tab === "deploy" || tab === "versoes") {
      if (tab === "deploy") setDeployOpen(true);
      else setVersionsOpen(true);
      nav(`/p/${p}/t/${t}`, { replace: true });
    }
  }, [tab, p, t]);

  // draft local (editável) + versão sendo vista (somente leitura)
  const [spec, setSpec] = useState<Spec | null>(null);
  const [draftHash, setDraftHash] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"saved" | "pending" | "saving">("saved");
  useEffect(() => {
    if (data && spec === null) {
      setSpec(data.draft.spec);
      setDraftHash(data.draft.hash);
    }
  }, [data, spec]);
  const { data: viewed } = useQuery<any>(refKey !== "draft" ? `${path}/versions/${refKey}` : null);

  const pendingSpec = useRef<Spec | null>(null);
  const persist = useCallback(
    async (s: Spec) => {
      setSaveState("saving");
      try {
        const res = await api.put(`${path}/draft`, { spec: s });
        setDraftHash(res.hash);
        if (pendingSpec.current === s) {
          pendingSpec.current = null;
          setSaveState("saved");
        }
      } catch (e) {
        toastError(e);
        setSaveState("pending");
      }
    },
    [path]
  );
  const debounced = useDebounced<Spec>(persist, 450);
  const onSpecChange = (s: Spec) => {
    const next = syncVars(s);
    setSpec(next);
    pendingSpec.current = next;
    setSaveState("pending");
    debounced(next);
  };
  const flush = async () => {
    if (pendingSpec.current) await persist(pendingSpec.current);
  };
  const refreshDraftHash = async () => {
    const d = await api.get(`${path}/versions/draft`);
    setDraftHash(d.hash);
  };

  if (error) return <div className="page">{error.message}</div>;
  if (!data || !spec) return <div className="content"><div className="page"><div className="skeleton" style={{ height: 28, width: 240 }} /></div></div>;

  const meta = data.meta;
  const latest = data.versions[0];
  const draftChanged = !latest || draftHash !== latest.hash;
  const readOnly = refKey !== "draft";
  const shownSpec: Spec | null = readOnly ? viewed?.spec ?? null : spec;
  const base = `/p/${p}/t/${t}`;
  const tabs = [
    { key: "editor", label: "Editor", href: base },
    { key: "avaliacoes", label: "Avaliações", href: `${base}/avaliacoes` },
    { key: "usage", label: "Usage", href: `${base}/usage` },
    { key: "gosto", label: "Gosto", href: `${base}/gosto` },
  ];
  const setRef = (r: string) => nav(r === "draft" ? base : `${base}?v=${r}`);

  return (
    <>
      <div className="topbar">
        <div className="crumbs">
          <Link href={`/p/${p}`} className="ellipsis crumb-project">{project?.name ?? p}</Link>
          <span className="crumb-sep">/</span>
          <KindIcon kind={meta.kind} />
          <span className="cur ellipsis" title={meta.name}>{meta.name}</span>
        </div>
        <nav className="tabs">
          {tabs.slice(0, 1).map((x) => (
            <Link key={x.key} href={x.href} className={cx("tab", tab === x.key && "active")}>
              {x.label}
            </Link>
          ))}
          <button className={cx("tab tab-btn", versionsOpen && "active")} onClick={() => setVersionsOpen(true)}>
            Versões
            {data.versions.length ? <span className="count">{data.versions.length}</span> : null}
          </button>
          {tabs.slice(1).map((x) => (
            <Link key={x.key} href={x.href} className={cx("tab", tab === x.key && "active")}>
              {x.label}
            </Link>
          ))}
        </nav>
        <div className="grow" />
        <div className="topbar-actions">
          <button className="channel-strip-btn" onClick={() => setDeployOpen(true)} title="Publicar e ver como chamar a API">
            <ChannelStrip channels={meta.channels} />
            <Rocket size={13} />
          </button>
          {tab === "editor" && (
            <>
              <span className="tb-sep" />
              <RefSwitcher refKey={refKey} versions={data.versions} channels={meta.channels} onChange={setRef} draftChanged={draftChanged} />
              {!readOnly && (
                <SaveVersionButton path={path} disabled={!draftChanged || saveState !== "saved"} saveState={saveState} draftChanged={draftChanged} latest={latest?.n} onBeforeSave={flush} onSaved={() => (invalidate(path), invalidate("/api/projects"))} />
              )}
            </>
          )}
        </div>
      </div>
      <div className="content" style={tab === "editor" ? { overflow: "hidden" } : undefined}>
        {tab === "editor" && shownSpec && (
          <ResizableSplit>
            <div className="pane">
              {readOnly && (
                <div style={{ padding: "16px 26px 0" }}>
                  <div className="banner">
                    <GitCommitHorizontal size={15} />
                    <span className="grow">Você está vendo a <b>v{refKey}</b>, que é imutável. O playground roda essa versão.</span>
                    <Button size="sm" icon={<RotateCcw size={13} />} onClick={async () => {
                      try {
                        await api.post(`${path}/versions/${refKey}/restore`);
                        const d = await api.get(`${path}/versions/draft`);
                        setSpec(d.spec);
                        setDraftHash(d.hash);
                        invalidate(path);
                        toast(`v${refKey} restaurada no draft`);
                        setRef("draft");
                      } catch (e) { toastError(e); }
                    }}>Restaurar no draft</Button>
                    <Button size="sm" variant="ghost" icon={<ArrowLeft size={13} />} onClick={() => setRef("draft")}>Draft</Button>
                  </div>
                </div>
              )}
              {shownSpec.kind === "prompt" ? (
                <PromptEditor key={refKey} spec={shownSpec} onChange={onSpecChange} readOnly={readOnly} saveState={saveState} />
              ) : (
                <AgentEditor key={refKey} p={p} t={t} spec={shownSpec} onChange={onSpecChange} readOnly={readOnly} refKey={refKey} files={readOnly ? viewed?.files ?? [] : data.draft.files} onFilesChanged={() => (invalidate(path), refreshDraftHash())} saveState={saveState} />
              )}
            </div>
            <div className="pane">
              <Playground p={p} t={t} spec={shownSpec} refKey={refKey} beforeRun={flush} sessions={data.sessions} onRan={() => invalidate(path)} />
            </div>
          </ResizableSplit>
        )}
        {tab === "avaliacoes" && <EvalsTab p={p} t={t} data={data} />}
        {(tab === "usage" || tab === "logs") && <TargetLogs p={p} t={t} />}
        {tab === "gosto" && <TasteTab p={p} t={t} data={data} />}
      </div>
      {deployOpen && <DeployModal p={p} t={t} data={data} onClose={() => setDeployOpen(false)} />}
      {versionsOpen && (
        <Modal open onClose={() => setVersionsOpen(false)} title="Versões" width={1280}>
          <div className="versions-modal">
            <VersionsTab p={p} t={t} data={data} onClose={() => setVersionsOpen(false)} onRestored={async () => { const d = await api.get(`${path}/versions/draft`); setSpec(d.spec); setDraftHash(d.hash); }} />
          </div>
        </Modal>
      )}
    </>
  );
}

function TargetLogs({ p, t }: { p: string; t: string }) {
  const search = useSearch();
  const [open, setOpen] = useState<string | null>(new URLSearchParams(search).get("ex"));
  return (
    <div className="page">
      <LogsTable project={p} target={t} onOpen={setOpen} />
      <ExecutionDrawer id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

// Editor à esquerda, playground à direita; o divisor arrasta e a largura fica salva neste navegador.
// Duplo clique volta ao padrão.
const SPLIT_KEY = "atelie:split";
const SPLIT_DEFAULT = 52;
function ResizableSplit({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(() => {
    const v = Number(localStorage.getItem(SPLIT_KEY));
    return v >= 20 && v <= 80 ? v : SPLIT_DEFAULT;
  });
  const [dragging, setDragging] = useState(false);
  useEffect(() => localStorage.setItem(SPLIT_KEY, String(left)), [left]);
  const move = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Cada lado fica com pelo menos 400px, para os campos do editor e do playground não se espremerem.
    const min = Math.min(400, r.width / 2);
    const x = Math.min(r.width - min, Math.max(min, clientX - r.left));
    setLeft(Math.round((x / r.width) * 1000) / 10);
  };
  return (
    <div ref={ref} className={cx("split", dragging && "dragging")} style={{ "--split": `${left}%` } as React.CSSProperties}>
      {children}
      <div
        className="split-handle"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={left}
        title="Arraste para redimensionar · duplo clique volta ao padrão"
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(true);
        }}
        onPointerMove={(e) => dragging && move(e.clientX)}
        onPointerUp={(e) => {
          e.currentTarget.releasePointerCapture(e.pointerId);
          setDragging(false);
        }}
        onDoubleClick={() => setLeft(SPLIT_DEFAULT)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setLeft((v) => Math.max(20, v - 2));
          if (e.key === "ArrowRight") setLeft((v) => Math.min(80, v + 2));
        }}
        tabIndex={0}
      />
    </div>
  );
}

export function ChannelStrip({ channels }: { channels: Record<string, number> }) {
  const entries = Object.entries(channels ?? {}).sort(([a], [b]) => (a === "production" ? -1 : b === "production" ? 1 : a.localeCompare(b)));
  if (!entries.length) return <span className="chan chan-off" title="Nenhum canal aponta para uma versão deste alvo"><Dot />não publicado</span>;
  return (
    <div className="channel-strip">
      {entries.map(([c, v]) => (
        <span key={c} className="chan" title={`${c} está na v${v}`}><Dot tone={channelTone(c) as any} />{c === "production" ? "prod" : c}<span className="chan-v">v{v}</span></span>
      ))}
    </div>
  );
}

function RefSwitcher({ refKey, versions, channels, onChange, draftChanged }: { refKey: string; versions: any[]; channels: Record<string, number>; onChange: (r: string) => void; draftChanged: boolean }) {
  const { open, setOpen, ref } = usePopover();
  const chanOf = (n: number) => Object.entries(channels).filter(([, v]) => v === n).map(([c]) => c);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="btn btn-sm btn-ghost ref-btn" onClick={() => setOpen(!open)} title={refKey === "draft" ? (draftChanged ? "O draft tem mudanças que ainda não viraram versão" : "O draft é igual à última versão") : "Vendo uma versão salva"}>
        {refKey === "draft" ? (
          <>
            <span className={cx("ref-dot", draftChanged && "on")} />
            <span>Draft</span>
            <span className="faint">{draftChanged ? (versions.length ? "alterado" : "sem versões") : `= v${versions[0]?.n}`}</span>
          </>
        ) : (
          <>
            <GitCommitHorizontal size={13} />
            <span>Versão v{refKey}</span>
          </>
        )}
        <ChevronDown size={13} className="faint" />
      </button>
      {open && (
        <div className="popover" style={{ top: 32, right: 0, width: 320, maxHeight: 380, overflow: "auto", transformOrigin: "top right" }}>
          <div className={cx("pop-item", refKey === "draft" && "hl")} onClick={() => (onChange("draft"), setOpen(false))}>
            <CircleDot size={14} />
            <span className="grow">Draft</span>
            <span className="faint" style={{ fontSize: 12 }}>{draftChanged ? "com mudanças" : "igual à última"}</span>
          </div>
          <div className="pop-sep" />
          {versions.map((v) => (
            <div key={v.n} className={cx("pop-item", refKey === String(v.n) && "hl")} onClick={() => (onChange(String(v.n)), setOpen(false))}>
              <span className="mono" style={{ width: 28, fontWeight: 600 }}>v{v.n}</span>
              <span className="grow ellipsis">{v.message}</span>
              {chanOf(v.n).map((c) => <Badge key={c} tone={channelTone(c) as any}>{c === "production" ? "prod" : c}</Badge>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SaveVersionButton({ path, disabled, saveState, draftChanged, latest, onBeforeSave, onSaved }: { path: string; disabled: boolean; saveState: string; draftChanged: boolean; latest?: number; onBeforeSave: () => Promise<void>; onSaved: () => void }) {
  const { open, setOpen, ref } = usePopover();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (channel?: string) => {
    setBusy(true);
    try {
      await onBeforeSave();
      const r = await api.post(`${path}/versions`, { message: msg });
      if (channel) {
        const d = await api.post(`${path}/deploy`, { channel, version: r.version.n });
        toast(`v${r.version.n} salva e publicada em ${channel}`, `no ar em ${d.liveMs} ms`);
      } else toast(r.created ? `v${r.version.n} salva` : "Nada mudou desde a última versão", r.created ? `hash ${r.version.hash}` : undefined);
      setMsg("");
      setOpen(false);
      onSaved();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div ref={ref} className="row" style={{ position: "relative", gap: 10 }}>
      <span className="save-state" title="O draft é salvo sozinho enquanto você edita">
        {saveState === "saved" ? (draftChanged ? <><Check size={12} />salvo</> : null) : <><span className="spinner spinner-xs" />salvando</>}
      </span>
      <Button size="sm" variant="primary" icon={<GitCommitHorizontal size={14} />} disabled={disabled && !open} onClick={() => setOpen(!open)}>
        Salvar v{(latest ?? 0) + 1}
      </Button>
      {open && (
        <div className="popover" style={{ top: 34, right: 0, width: 340, padding: 12, transformOrigin: "top right" }}>
          <div className="label" style={{ marginBottom: 8 }}>O que mudou?</div>
          <input className="input" autoFocus value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder={`Versão ${(latest ?? 0) + 1}`} />
          <div className="field-hint" style={{ margin: "8px 0 12px" }}>A versão fica imutável. Publicar é só mover um canal para ela.</div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <Button size="sm" loading={busy} onClick={() => save("staging")} icon={<Rocket size={13} />}>Salvar e publicar em staging</Button>
            <Button size="sm" variant="primary" loading={busy} onClick={() => save()}>Salvar</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- editor de prompt ----------

const REASONING = ["", "none", "minimal", "low", "medium", "high"];

function PromptEditor({ spec, onChange, readOnly }: { spec: PromptSpec; onChange: (s: Spec) => void; readOnly: boolean; saveState: string }) {
  const set = (patch: Partial<PromptSpec>) => onChange({ ...spec, ...patch });
  const isImageModel = /image/.test(spec.model);
  const setParam = (patch: Partial<PromptSpec["params"]>) => set({ params: { ...spec.params, ...patch } });
  return (
    <div className="pane-inner ed-doc">
      <section className="ed-card ed-model">
        <ModelPicker value={spec.model} kinds={["text", "image"]} disabled={readOnly} onChange={(m) => set({ model: m, output: /image/.test(m) ? { format: "image" } : spec.output.format === "image" ? { format: "text" } : spec.output })} />
        <div className="ed-params">
          {!isImageModel ? (
            <>
              <label className="ed-param">
                <span>Raciocínio</span>
                <select className="select select-sm" disabled={readOnly} value={spec.params.reasoning ?? ""} onChange={(e) => setParam({ reasoning: e.target.value as any })}>
                  {REASONING.map((r) => <option key={r} value={r}>{r || "padrão"}</option>)}
                </select>
              </label>
              <label className="ed-param">
                <span>Temperatura</span>
                <input className="input input-sm num" disabled={readOnly} type="number" step="0.1" min="0" max="2" placeholder="padrão" value={spec.params.temperature ?? ""} onChange={(e) => setParam({ temperature: e.target.value === "" ? undefined : Number(e.target.value) })} />
              </label>
              <label className="ed-param">
                <span>Máx. tokens</span>
                <input className="input input-sm num" disabled={readOnly} type="number" min="1" placeholder="padrão" value={spec.params.maxTokens ?? ""} onChange={(e) => setParam({ maxTokens: e.target.value === "" ? undefined : Number(e.target.value) })} />
              </label>
            </>
          ) : (
            <label className="ed-param">
              <span>Tamanho</span>
              <select className="select select-sm" disabled={readOnly} value={spec.params.size ?? "1024x1024"} onChange={(e) => setParam({ size: e.target.value })}>
                {["1024x1024", "1536x1024", "1024x1536", "auto"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          )}
        </div>
      </section>

      <section className="ed-card">
        <header className="ed-head">
          <span className="ed-title">Instruções do sistema</span>
          <span className="ed-hint">quem o modelo é, regras e formato</span>
        </header>
        <CodeEditor className="flat" prose value={spec.system} readOnly={readOnly} onChange={(v) => set({ system: v })} minHeight={120} placeholder="Você é…" />
      </section>

      <section className="ed-card">
        <header className="ed-head">
          <span className="ed-title">Mensagem <span className="ed-optional">opcional</span></span>
          <span className="ed-hint">use <span className="var-token mono">{"{{variavel}}"}</span> para cada entrada</span>
        </header>
        <CodeEditor className="flat" prose value={spec.prompt} readOnly={readOnly} onChange={(v) => set({ prompt: v })} minHeight={72} placeholder="Vazia, as instruções do sistema viram a mensagem." />
      </section>

      <VariablesBlock spec={spec} onChange={onChange} readOnly={readOnly} />

      <section className="ed-card">
        <header className="ed-head">
          <span className="ed-title">Saída</span>
          <Seg
            value={spec.output.format}
            onChange={(f) => !readOnly && set({ output: { ...spec.output, format: f } })}
            options={[
              { value: "text", label: "Texto", icon: <Type size={12} /> },
              { value: "json", label: "JSON", icon: <Braces size={12} /> },
              { value: "image", label: "Imagem", icon: <ImageIcon size={12} /> },
            ]}
          />
        </header>
        {spec.output.format === "text" && <div className="ed-note">O modelo responde em texto livre.</div>}
        {spec.output.format === "json" && (
          <div className="ed-body">
            <SchemaEditor schema={spec.output.schema} readOnly={readOnly} onChange={(schema) => set({ output: { ...spec.output, schema } })} />
          </div>
        )}
        {spec.output.format === "image" && (isImageModel ? <div className="ed-note">O modelo devolve imagens geradas.</div> : <div className="ed-note" style={{ color: "var(--amber)" }}>Escolha um modelo de imagem (GPT Image ou Nano Banana) para gerar imagens.</div>)}
      </section>
    </div>
  );
}

function VariablesBlock({ spec, onChange, readOnly }: { spec: Spec; onChange: (s: Spec) => void; readOnly: boolean }) {
  const vars = Object.entries(spec.variables ?? {});
  if (!vars.length) return null;
  return (
    <section className="ed-card">
      <header className="ed-head">
        <span className="ed-title">Entradas <span className="ed-count">{vars.length}</span></span>
        <span className="ed-hint">viram os campos da API</span>
      </header>
      <div className="var-list">
        {vars.map(([name, def]) => (
          <div key={name} className="var-row">
            <span className="var-name">{name}</span>
            <input className="input input-sm var-desc" disabled={readOnly} placeholder="Descrição para a documentação da API" value={def.description ?? ""} onChange={(e) => onChange({ ...spec, variables: { ...spec.variables, [name]: { ...def, description: e.target.value } } } as Spec)} />
            <select className="select select-sm var-type" disabled={readOnly} value={def.type} onChange={(e) => onChange({ ...spec, variables: { ...spec.variables, [name]: { ...def, type: e.target.value as VarType } } } as Spec)}>
              <option value="text">texto</option>
              <option value="image">imagem</option>
              <option value="json">JSON</option>
            </select>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- editor de agente ----------

function AgentEditor({ p, t, spec, onChange, readOnly, refKey, files, onFilesChanged }: { p: string; t: string; spec: AgentSpec; onChange: (s: Spec) => void; readOnly: boolean; refKey: string; files: ContextFile[]; onFilesChanged: () => void; saveState: string }) {
  const [view, setView] = useState<"files" | "config">("files");
  const set = (patch: Partial<AgentSpec>) => onChange({ ...spec, ...patch });
  return (
    <>
      <div className="pane-head">
        <Seg value={view} onChange={setView} options={[{ value: "files", label: `Contexto · ${files.length} arquivos`, icon: <Folder size={12} /> }, { value: "config", label: "Tarefa e runtime", icon: <Bot size={12} /> }]} />
        <span className="row faint" style={{ fontSize: 12 }}>
          <Badge tone={spec.mode === "stateful" ? "blue" : "plain"}>{spec.mode}</Badge>
          <span className="mono">{spec.model}</span>
        </span>
      </div>
      <div className="pane-inner" style={{ paddingTop: 18 }}>
        {view === "files" ? (
          <>
            <div className="field-hint" style={{ marginBottom: 12 }}>
              Estes arquivos são o "prompt" do agente: a cada execução, a pasta inteira é copiada para um workspace isolado. <span className="mono">AGENTS.md</span> vira as instruções; <span className="mono">skills/</span> vira skills do runtime.
            </div>
            <FilesEditor p={p} t={t} files={files} refKey={refKey} readOnly={readOnly} onChanged={onFilesChanged} />
          </>
        ) : (
          <>
            <div className="block">
              <div className="block-head"><span className="label">Runtime e modelo</span></div>
              <ModelPicker value={spec.model} kinds={["agent"]} disabled={readOnly} onChange={(m) => set({ model: m, runtime: m.startsWith("codex/") ? "codex" : "claude-code" })} />
            </div>
            <div className="block">
              <div className="block-head"><span className="label">Estado entre execuções</span></div>
              <div className="grid-2">
                {(["stateless", "stateful"] as const).map((m) => (
                  <button key={m} type="button" disabled={readOnly} onClick={() => set({ mode: m })} className="card" style={{ padding: 14, textAlign: "left", cursor: readOnly ? "default" : "pointer", borderColor: spec.mode === m ? "var(--g8)" : undefined, background: spec.mode === m ? "var(--surface-2)" : undefined }}>
                    <div className="row" style={{ marginBottom: 6 }}><Dot tone={spec.mode === m ? "green" : undefined} /><b>{m === "stateless" ? "Stateless" : "Stateful"}</b></div>
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {m === "stateless" ? "Cada execução começa do zero numa cópia limpa do contexto. Reprodutível e cacheável." : "Cada sessão tem um workspace e uma conversa próprios, que continuam de uma chamada para a outra."}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="block">
              <div className="block-head"><span className="label">Tarefa de cada execução</span><span className="faint" style={{ fontSize: 12 }}>as <span className="var-token mono">{"{{variáveis}}"}</span> viram a entrada da API</span></div>
            <CodeEditor prose value={spec.task} readOnly={readOnly} onChange={(v) => set({ task: v })} minHeight={80} />
            </div>
            <VariablesBlock spec={spec} onChange={onChange} readOnly={readOnly} />
            <div className="block">
              <div className="block-head">
                <span className="label">Saída</span>
                <Seg value={spec.output.format === "json" ? "json" : "text"} onChange={(f) => !readOnly && set({ output: { ...spec.output, format: f } })} options={[{ value: "text", label: "Texto", icon: <Type size={12} /> }, { value: "json", label: "JSON", icon: <Braces size={12} /> }]} />
              </div>
              {spec.output.format === "json" && (
                <SchemaEditor schema={spec.output.schema} readOnly={readOnly} onChange={(schema) => set({ output: { ...spec.output, schema } })} />
              )}
            </div>
            <div className="block">
              <div className="block-head"><span className="label">Limites e isolamento</span></div>
              <div className="param-grid">
                <Field label="Máx. turnos"><input className="input input-sm mono" type="number" disabled={readOnly} value={spec.limits.maxTurns} onChange={(e) => set({ limits: { ...spec.limits, maxTurns: Number(e.target.value) } })} /></Field>
                <Field label="Timeout (s)"><input className="input input-sm mono" type="number" disabled={readOnly} value={spec.limits.timeoutSec} onChange={(e) => set({ limits: { ...spec.limits, timeoutSec: Number(e.target.value) } })} /></Field>
                <Field label="Sandbox">
                  <select className="select select-sm" disabled={readOnly} value={spec.sandbox} onChange={(e) => set({ sandbox: e.target.value as any })}>
                    <option value="seatbelt">Seatbelt (macOS)</option>
                    <option value="none">sem sandbox</option>
                  </select>
                </Field>
              </div>
              <div className="field-hint" style={{ marginTop: 8 }}>No Seatbelt o agente só escreve no próprio workspace; tentativas de escrever fora falham com "operation not permitted".</div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function FilesEditor({ p, t, files, refKey, readOnly, onChanged }: { p: string; t: string; files: ContextFile[]; refKey: string; readOnly: boolean; onChanged: () => void }) {
  const path = `/api/projects/${p}/targets/${t}/files`;
  const [sel, setSel] = useState<string | null>(null);
  const [content, setContent] = useState<string>("");
  const [binary, setBinary] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);
  const selected = sel && files.some((f) => f.path === sel) ? sel : files.find((f) => /^(AGENTS|CLAUDE)\.md$/.test(f.path))?.path ?? files[0]?.path ?? null;
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    api.get(`${path}?ref=${refKey}&path=${encodeURIComponent(selected)}`).then((r) => {
      if (!alive) return;
      setContent(r.content);
      setBinary(r.binary);
    }).catch(toastError);
    return () => { alive = false; };
  }, [selected, refKey, path]);
  const save = useDebounced<{ path: string; content: string }>(async (f) => {
    try {
      await api.put(path, f);
      onChanged();
    } catch (e) { toastError(e); }
  }, 500);
  const tree = useMemo(() => {
    const groups = new Map<string, ContextFile[]>();
    for (const f of files) {
      const dir = f.path.includes("/") ? f.path.slice(0, f.path.lastIndexOf("/")) : "";
      groups.set(dir, [...(groups.get(dir) ?? []), f]);
    }
    return [...groups.entries()].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
  }, [files]);
  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.put(path, { path: name, content: name.endsWith(".md") ? `# ${name.split("/").pop()}\n` : "" });
      setNewOpen(false);
      setNewName("");
      setSel(name);
      onChanged();
    } catch (e) { toastError(e); }
  };
  const upload = async (list: FileList | null) => {
    for (const f of Array.from(list ?? [])) {
      const dataUrl = await fileToDataUrl(f);
      await api.put(path, { path: `data/${f.name}`, content: dataUrl.split(",")[1], encoding: "base64" }).catch(toastError);
    }
    onChanged();
    toast("Arquivos enviados para data/");
  };
  return (
    <div className="files-split">
      <div className="filetree">
        {tree.map(([dir, fs]) => (
          <div key={dir}>
            {dir && <div className="folder"><Folder size={12} />{dir}/</div>}
            {fs.map((f) => (
              <div key={f.path} className={cx("file", selected === f.path && "active")} style={dir ? { paddingLeft: 18 } : undefined} onClick={() => setSel(f.path)}>
                <FileText size={13} />
                <span className="name">{f.path.split("/").pop()}</span>
                {!readOnly && (
                  <span className="x" onClick={async (e) => {
                    e.stopPropagation();
                    if (!confirm(`Apagar ${f.path}?`)) return;
                    await api.del(`${path}?path=${encodeURIComponent(f.path)}`).catch(toastError);
                    onChanged();
                  }}><Trash2 size={12} /></span>
                )}
              </div>
            ))}
          </div>
        ))}
        {!readOnly && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)" }} className="col">
            <div className="file" onClick={() => setNewOpen(true)}><FilePlus size={13} /><span className="name">Novo arquivo</span></div>
            <div className="file" onClick={() => uploadRef.current?.click()}><Upload size={13} /><span className="name">Enviar dados</span></div>
            <input ref={uploadRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} />
          </div>
        )}
      </div>
      <div className="file-editor">
        <div className="file-editor-head">
          <span>{selected ?? "—"}</span>
          {readOnly && <Badge>somente leitura</Badge>}
        </div>
        {selected && !binary && (
          <CodeEditor
            key={selected + refKey}
            className="flat"
            numbers
            language={selected.endsWith(".json") ? "json" : selected.endsWith(".md") ? "markdown" : "text"}
            value={content}
            readOnly={readOnly}
            minHeight={420}
            onChange={(v) => { setContent(v); save({ path: selected, content: v }); }}
          />
        )}
        {binary && <div className="empty">Arquivo binário.</div>}
        {!files.length && <Empty title="Contexto vazio">Crie um AGENTS.md com as instruções do agente.</Empty>}
      </div>
      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Novo arquivo" footer={<><Button variant="ghost" onClick={() => setNewOpen(false)}>Cancelar</Button><Button variant="primary" onClick={create} disabled={!newName.trim()}>Criar</Button></>}>
        <Field label="Caminho" hint="Ex.: skills/revisar-contrato/SKILL.md, data/tabela.csv, scripts/calc.py">
          <input className="input mono" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
        </Field>
      </Modal>
    </div>
  );
}

// ---------- playground ----------

function loadInputs(key: string) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "{}");
  } catch {
    return {};
  }
}

type RunState = { model: string; status: "running" | "done" | "error"; exec?: Execution; error?: string; events: TraceEvent[]; text?: string; startedAt: number };

function Playground({ p, t, spec, refKey, beforeRun, sessions, onRan }: { p: string; t: string; spec: Spec; refKey: string; beforeRun: () => Promise<void>; sessions: any[]; onRan: () => void }) {
  const storeKey = `atelie:inputs:${p}/${t}`;
  const [inputs, setInputs] = useState<Record<string, any>>(() => loadInputs(storeKey));
  const [extraModels, setExtraModels] = useState<string[]>([]);
  const [cache, setCache] = useState(spec.kind === "prompt");
  const [session, setSession] = useState<string>(() => localStorage.getItem(`${storeKey}:session`) ?? "teste");
  const [runs, setRuns] = useState<RunState[]>([]);
  const [addCase, setAddCase] = useState<Execution | null>(null);
  const [openEx, setOpenEx] = useState<string | null>(null);
  const search = useSearch();
  const exParam = new URLSearchParams(search).get("ex");
  const { data: history } = useQuery<Execution[]>(`/api/executions?project=${p}&target=${t}&source=playground&limit=6`);
  const aborter = useRef<AbortController | null>(null);

  useEffect(() => localStorage.setItem(storeKey, JSON.stringify(inputs)), [inputs, storeKey]);
  useEffect(() => localStorage.setItem(`${storeKey}:session`, session), [session, storeKey]);
  useEffect(() => {
    if (!exParam) return;
    api.get<Execution>(`/api/executions/${exParam}`).then((e) => {
      setInputs(e.inputs);
      if (e.session) setSession(e.session);
      setRuns([{ model: e.model, status: e.status === "error" ? "error" : "done", exec: e, error: e.error ?? undefined, events: e.trace ?? [], startedAt: e.createdAt }]);
    }).catch(() => {});
  }, [exParam]);

  const vars = Object.entries(spec.variables ?? {});
  const missing = vars.filter(([n]) => inputs[n] === undefined || inputs[n] === "").map(([n]) => n);
  const status = useStatus();
  const agentsOff = spec.kind === "agent" && status?.agents && !status.agents.enabled ? status.agents.reason : null;
  const running = runs.some((r) => r.status === "running");
  const url = `/api/projects/${p}/targets/${t}/run`;

  const run = async () => {
    if (missing.length || running || agentsOff) return;
    await beforeRun();
    const models = [spec.model, ...extraModels.filter((m) => m !== spec.model)];
    const body = (model: string) => ({ ref: refKey, inputs, model: model === spec.model ? undefined : model, cache, session: spec.kind === "agent" && spec.mode === "stateful" ? session : undefined });
    const initial: RunState[] = models.map((m) => ({ model: m, status: "running", events: [], startedAt: Date.now() }));
    setRuns(initial);
    const update = (i: number, patch: Partial<RunState> | ((r: RunState) => Partial<RunState>)) =>
      setRuns((rs) => rs.map((r, j) => (j === i ? { ...r, ...(typeof patch === "function" ? patch(r) : patch) } : r)));
    aborter.current = new AbortController();
    await Promise.all(
      models.map(async (m, i) => {
        try {
          await streamPost(url, body(m), (ev, data) => {
            if (ev === "event") update(i, (r) => ({ events: [...r.events, data] }));
            else if (ev === "delta") update(i, (r) => ({ text: data.reset ? "" : (r.text ?? "") + (data.text ?? "") }));
            else if (ev === "done") update(i, { status: data.error ? "error" : "done", exec: data, error: data.error ?? undefined, events: data.trace ?? [] });
            else if (ev === "error") update(i, { status: "error", error: data.error });
          }, aborter.current!.signal);
        } catch (e: any) {
          update(i, { status: "error", error: e?.name === "AbortError" ? "Parado antes do fim." : e.message });
        }
      })
    );
    invalidate(`/api/executions?project=${p}&target=${t}`);
    onRan();
  };

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        run();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  const stateful = spec.kind === "agent" && spec.mode === "stateful";
  return (
    <>
      <div className="pane-head">
        <span className="row" style={{ gap: 10 }}>
          <b>Playground</b>
          <Badge tone={refKey === "draft" ? "violet" : "plain"}>{refKey === "draft" ? "draft" : `v${refKey}`}</Badge>
        </span>
        <span className="row" style={{ gap: 14 }}>
          <label className="row faint" style={{ gap: 7, fontSize: 12, cursor: "pointer" }} title="Reusa a resposta se entrada, versão e modelo forem idênticos">
            <Zap size={12} /> cache <Switch on={cache && !stateful} onChange={setCache} />
          </label>
        </span>
      </div>
      <div className="pane-inner">
        {agentsOff && (
          <div className="banner err">
            <ShieldOff size={15} />
            <span className="grow">{agentsOff}</span>
          </div>
        )}
        {stateful && (
          <div className="pg-input">
            <label><span className="label">Sessão</span><span className="faint" style={{ fontSize: 12 }}>workspace e conversa persistem por sessão</span></label>
            <div className="row">
              <input className="input mono" list={`sessions-${t}`} value={session} onChange={(e) => setSession(e.target.value.replace(/[^\w.@-]/g, ""))} />
              <datalist id={`sessions-${t}`}>{sessions.map((s) => <option key={s.key} value={s.key}>{s.runs} execuções</option>)}</datalist>
              <Button icon={<Plus size={14} />} onClick={() => setSession(`sessao-${Math.random().toString(36).slice(2, 7)}`)}>Nova</Button>
            </div>
            {sessions.find((s) => s.key === session) ? (
              <div className="field-hint" style={{ marginTop: 6 }}>Continua a sessão <b>{session}</b> ({sessions.find((s) => s.key === session).runs} execuções, última {timeAgo(sessions.find((s) => s.key === session).updated_at)}).</div>
            ) : (
              <div className="field-hint" style={{ marginTop: 6 }}>Sessão nova: começa com uma cópia limpa do contexto.</div>
            )}
          </div>
        )}
        {vars.map(([name, def]) => (
          <div key={name} className="pg-input">
            <label>
              <span className="var-name">{name}</span>
              <span className="faint" style={{ fontSize: 12 }}>{def.description || (def.type === "image" ? "imagem" : def.type === "json" ? "JSON" : "texto")}</span>
            </label>
            {def.type === "image" ? (
              <ImageInput value={inputs[name]} onChange={(v) => setInputs({ ...inputs, [name]: v })} />
            ) : def.type === "json" ? (
              <CodeEditor language="json" value={typeof inputs[name] === "string" ? inputs[name] : JSON.stringify(inputs[name] ?? {}, null, 2)} onChange={(v) => { try { setInputs({ ...inputs, [name]: JSON.parse(v) }); } catch { setInputs({ ...inputs, [name]: v }); } }} minHeight={70} onSubmit={run} />
            ) : (
              <AutoTextarea value={inputs[name] ?? ""} onChange={(v) => setInputs({ ...inputs, [name]: v })} placeholder={`${name}…`} minRows={2} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); run(); } }} />
            )}
          </div>
        ))}
        {!vars.length && <div className="field-hint" style={{ marginBottom: 14 }}>Sem variáveis: o prompt roda sempre igual. Adicione <span className="var-token mono">{"{{algo}}"}</span> para receber entradas.</div>}

        <div className="run-bar">
          <Button variant="primary" icon={running ? undefined : <Play size={14} />} loading={running} disabled={missing.length > 0 || !!agentsOff} onClick={run}>
            {running ? (spec.kind === "agent" ? "Agente trabalhando…" : "Rodando…") : "Executar"} {!running && <span className="kbd">⌘↵</span>}
          </Button>
          {running && <Button variant="ghost" size="sm" onClick={() => aborter.current?.abort()}>Parar</Button>}
          {spec.kind === "prompt" && (
            <CompareModels main={spec.model} models={extraModels} onChange={setExtraModels} imageOnly={/image/.test(spec.model)} />
          )}
          <span className="grow" />
          {missing.length > 0 && !running && <span className="run-hint">Preencha {missing.map((m, i) => <React.Fragment key={m}>{i ? ", " : ""}<span className="var-name">{m}</span></React.Fragment>)}</span>}
        </div>

        <div className="col" style={{ gap: 14 }}>
          {runs.map((r, i) => (
            <RunResult key={i + r.model + r.startedAt} r={r} kind={spec.kind} format={spec.kind === "prompt" ? spec.output.format : "text"} showModel={runs.length > 1} onAddCase={setAddCase} onOpen={setOpenEx} />
          ))}
        </div>

        {!runs.length && history && history.length > 0 && (
          <div className="pg-history">
            <div className="pg-history-title">Execuções recentes</div>
            {history.map((h) => (
              <div key={h.id} className="history-item" onClick={() => { setInputs(h.inputs); setRuns([{ model: h.model, status: h.status === "error" ? "error" : "done", exec: h, error: h.error ?? undefined, events: h.trace ?? [], startedAt: h.createdAt }]); }}>
                <Dot tone={h.status === "error" ? "red" : "green"} />
                <span className="grow ellipsis">{preview(Object.values(h.inputs)[0], 70) || "(sem entrada)"}</span>
                <span className="mono faint" style={{ fontSize: 11.5 }}>{h.version ? `v${h.version}` : "draft"} · {timeAgo(h.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
        {!runs.length && history && !history.length && (
          <div className="pg-empty">
            <span className="pg-empty-icon"><Play size={15} /></span>
            <span className="pg-empty-title">O resultado aparece aqui</span>
            <span>Preencha {vars.length ? "as entradas" : "o prompt"} e clique em Executar ou aperte <span className="kbd-inline">⌘↵</span>.</span>
          </div>
        )}
      </div>
      {addCase && <AddCaseModal open onClose={() => setAddCase(null)} exec={addCase} />}
      <ExecutionDrawer id={openEx} onClose={() => setOpenEx(null)} />
    </>
  );
}

function RunResult({ r, kind, format, showModel, onAddCase, onOpen }: { r: RunState; kind: string; format: string; showModel: boolean; onAddCase: (e: Execution) => void; onOpen: (id: string) => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (r.status !== "running") return;
    const iv = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(iv);
  }, [r.status]);
  const e = r.exec;
  return (
    <div className={cx("result", r.status === "error" && "error")}>
      <div className="result-head">
        <span className="row" style={{ gap: 8, minWidth: 0 }}>
          {r.status === "running" ? <span className="spinner" /> : <Dot tone={r.status === "error" ? "red" : "green"} />}
          <span className="mono ellipsis" style={{ fontSize: 12 }}>{showModel || kind === "agent" ? r.model : "Resultado"}</span>
          {r.status === "running" && <span className="mono faint" style={{ fontSize: 12 }}>{fmtMs(now - r.startedAt)}</span>}
        </span>
        {e && (
          <span className="row" style={{ gap: 4 }}>
            {!e.error && <Button size="xs" variant="ghost" onClick={() => onAddCase(e)}>Virar caso de teste</Button>}
            <Button size="xs" variant="ghost" onClick={() => onOpen(e.id)}>Detalhes</Button>
          </span>
        )}
      </div>
      {kind === "agent" && r.events.length > 0 && (
        <div className="result-body" style={{ borderBottom: "1px solid var(--border)", maxHeight: 420 }}>
          <TraceView events={r.events} live={r.status === "running"} />
        </div>
      )}
      {r.status !== "running" && (
        <div className="result-body">
          {r.error ? (
            <>
              {!e && r.text && <div className="text-out" style={{ color: "var(--text-1)", marginBottom: 10 }}>{r.text}</div>}
              <div className="text-out">{r.error}</div>
            </>
          ) : e ? <OutputView format={e.outputFormat} value={e.output} /> : null}
        </div>
      )}
      {r.status === "running" && kind !== "agent" && (
        <div className="result-body">
          {r.text ? (
            format === "text" ? <div className="streaming"><OutputView format="text" value={r.text} /></div> : <div className="text-out mono streaming">{r.text}</div>
          ) : (
            <><div className="skeleton" style={{ height: 14, width: "70%", marginBottom: 8 }} /><div className="skeleton" style={{ height: 14, width: "45%" }} /></>
          )}
        </div>
      )}
      {e && (
        <div className="result-foot">
          <MetaChips e={e} showModel={false} />
          {e.files && e.files.length > 0 && <span className="chip"><FileText size={11} />{e.files.length} arquivo(s)</span>}
          {e.session && <span className="chip">sessão {e.session}</span>}
        </div>
      )}
    </div>
  );
}

function CompareModels({ main, models, onChange, imageOnly }: { main: string; models: string[]; onChange: (m: string[]) => void; imageOnly: boolean }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
      {models.map((m) => (
        <span key={m} className="chip">{m}<X size={11} style={{ cursor: "pointer" }} onClick={() => onChange(models.filter((x) => x !== m))} /></span>
      ))}
      {adding ? (
        <div style={{ width: 260 }}>
          <ModelPicker defaultOpen onClose={() => setAdding(false)} value={main} kinds={imageOnly ? ["image"] : ["text"]} onChange={(m) => { if (m !== main && !models.includes(m)) onChange([...models, m]); setAdding(false); }} />
        </div>
      ) : (
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setAdding(true)}>comparar modelo</Button>
      )}
    </div>
  );
}

function ImageInput({ value, onChange }: { value: any; onChange: (v: string) => void }) {
  const [over, setOver] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const take = async (f?: File | null) => f && onChange(await fileToDataUrl(f));
  return (
    <div
      className={cx("drop", over && "over")}
      onClick={() => ref.current?.click()}
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files[0]); }}
      onPaste={(e) => take(e.clipboardData.files[0])}
      tabIndex={0}
    >
      {value ? <img src={typeof value === "string" ? value : value.url} alt="" /> : <><ImagePlus size={18} /><span>Arraste, cole ou clique para escolher uma imagem</span></>}
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => take(e.target.files?.[0])} />
    </div>
  );
}

export { Clock };
